import React, { useEffect, useRef } from "react";

import { formatRelative } from "../../utils/formatters";

const AUTO_SCROLL_PX_PER_SEC = 40;

const CommentsBanner = ({ banner, projectId, navigate }) => {
  const scrollRef = useRef(null);
  const pausedRef = useRef(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !banner?.households?.length) return;
    let raf;
    let last = performance.now();
    let direction = 1;
    const step = (now) => {
      const dt = now - last;
      last = now;
      const overflowing = el.scrollWidth > el.clientWidth + 1;
      if (!pausedRef.current && overflowing) {
        el.scrollLeft += (dt / 1000) * AUTO_SCROLL_PX_PER_SEC * direction;
        if (el.scrollLeft + el.clientWidth >= el.scrollWidth - 0.5) direction = -1;
        else if (el.scrollLeft <= 0.5) direction = 1;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [banner?.households?.length]);

  if (!banner || !banner.count) return null;

  return (
    <div className="mb-5 rounded-2xl bg-amber-50 border border-amber-200 px-5 py-4 flex items-center gap-4 overflow-hidden">
      <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center">
        <svg
          className="w-5 h-5 text-amber-800"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      </div>
      <div className="flex-shrink-0">
        <div className="text-sm font-semibold text-amber-900 whitespace-nowrap">
          {banner.count} Household{banner.count === 1 ? "" : "s"} have new comments or messages
        </div>
        <div className="text-xs text-amber-800/80 mt-0.5 whitespace-nowrap">
          Catch up on these before moving on
        </div>
      </div>

      <div
        ref={scrollRef}
        onMouseEnter={() => { pausedRef.current = true; }}
        onMouseLeave={() => { pausedRef.current = false; }}
        className="ml-auto w-[28rem] max-w-full overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      >
        <div className="flex items-center gap-2 flex-nowrap">
          {banner.households.map((h) => (
            <button
              key={h.id}
              type="button"
              onClick={() => navigate(`/project/${projectId}/house/${h.id}`)}
              className="text-xs font-medium px-3 py-1.5 rounded-full bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 whitespace-nowrap flex-shrink-0"
            >
              {h.name}
              {h.last_comment_age_hours != null && (
                <span className="text-amber-700/80 ml-1.5">
                  ({formatAge(h.last_comment_age_hours)})
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

const formatAge = (hours) => {
  if (hours < 1) return "just now";
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
};

void formatRelative;

export default CommentsBanner;
